import { ReactElement, ReactNode } from 'react';
import { XM_FONT_FAMILY } from './canvasUtils';
import { renderToMeasureHTML } from './renderStatic';

export interface MeasureCellItem {
  key: string;
  content: ReactNode | string | number | null | undefined;
  /** Pre-rendered HTML to use as innerHTML, skipping ReactDOMServer at measure time. */
  innerHTML?: string;
  styleClass?: string;
  newLines?: boolean;
}

/**
 * Measure the rendered width each column needs to fit its content without
 * clipping. Builds a single hidden `<table class="ant-table">` row off-screen,
 * lets the browser do real DOM layout, and reads `getBoundingClientRect().width`
 * per cell.
 */
export function measureColumnWidths(items: MeasureCellItem[]): Record<string, number> {
  if (!items.length) return {};

  // Measure with the canonical cell font, NOT the resolved font of a reference
  // cell. Reading from `document.querySelector('td.ant-table-cell')` made the
  // result depend on the DOM state at measure time: on a fresh refresh no cell
  // exists yet (it fell back to document.body's font), while navigating in found
  // a real cell — two different fonts, so the same table measured to different
  // column widths depending on how you arrived. XM_FONT_FAMILY is the antd
  // default stack (the theme sets no custom fontFamily) at the themed 12px, is a
  // per-OS stack so each platform picks the same native font the cell renders
  // with (incl. -apple-system on macOS), and is exactly what the canvas
  // trim-check uses — so DOM and canvas measurements now agree, deterministically.
  const font = `400 ${XM_FONT_FAMILY}`;

  const container = document.createElement('div');
  container.style.cssText = 'position:absolute;visibility:hidden;left:-9999px;top:-9999px;pointer-events:none';

  const table = document.createElement('table');
  table.className = 'ant-table';
  table.style.cssText = 'table-layout:auto;border-collapse:collapse';

  const tbody = document.createElement('tbody');
  tbody.className = 'ant-table-tbody';

  const tr = document.createElement('tr');
  tr.className = 'ant-table-row';

  const tds: { key: string; td: HTMLElement; }[] = [];
  for (const item of items) {
    const td = document.createElement('td');
    td.className = ['ant-table-cell', item.styleClass].filter(Boolean).join(' ');

    const whiteSpace = item.newLines ? 'pre-line' : 'nowrap';
    td.style.cssText = `font:${font};padding:4px 8px;white-space:${whiteSpace};box-sizing:border-box`;

    const content = item.content;
    if (item.innerHTML !== undefined) {
      td.innerHTML = item.innerHTML;
    } else if (content == null) {
      // leave td empty
    } else if (typeof content === 'string' || typeof content === 'number') {
      td.textContent = String(content);
    } else {
      try {
        td.innerHTML = renderToMeasureHTML(content as ReactElement);
      } catch {
        td.textContent = String(content);
      }
    }

    tr.appendChild(td);
    tds.push({ key: item.key, td });
  }

  tbody.appendChild(tr);
  table.appendChild(tbody);
  container.appendChild(table);
  document.body.appendChild(container);

  const widths: Record<string, number> = {};
  for (const { key, td } of tds) {
    widths[key] = Math.ceil(td.getBoundingClientRect().width);
  }

  document.body.removeChild(container);
  return widths;
}
